// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

// CRITICAL: Apply Form.io patches FIRST before any other imports
// This prevents "Cannot read properties of undefined (reading 'replace')" errors
// when Form.io components have undefined tooltip/placeholder/etc properties
import './utils/formio-patch';

// After a new deploy, the user's currently-loaded HTML still references the
// previous build's chunk filenames — those are gone on the server. The next
// lazy import (route change, modal open, etc.) fails with "Failed to fetch
// dynamically imported module". Vite emits `vite:preloadError` in exactly
// this case; we listen once, mark the page so we don't loop, and force a
// hard reload to fetch the new HTML + manifest.
if (typeof window !== 'undefined') {
  const RELOAD_FLAG = '__carepointPreloadReload';
  window.addEventListener('vite:preloadError', (event) => {
    if (sessionStorage.getItem(RELOAD_FLAG)) {
      // Already tried once this session — don't loop. Let the boundary show.
      return;
    }
    event.preventDefault?.();
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
    console.warn('[main] vite:preloadError — app updated; reloading to pick up new chunks');
    window.location.reload();
  });
}

import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import { Notifications } from '@mantine/notifications';
import '@mantine/notifications/styles.css';
import { MedplumClient } from '@medplum/core';
import { MedplumProvider } from '@medplum/react';
import '@medplum/react/styles.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router';
import { App } from './App';
import { ChunkLoadErrorBoundary } from './components/ChunkLoadErrorBoundary';
import { AppThemeProvider } from './contexts/AppThemeContext';
import { DynamicMantineProvider } from './components/DynamicMantineProvider';
import { getTenantConfig } from './config/tenant';

// Initialize i18n - must be imported before components that use translations
import './i18n';

// API Configuration
// All requests go directly to self-hosted Medplum
// Organization filtering is handled client-side via OrganizationContext.applyOrganizationFilter()
// Project-level isolation is provided by Medplum's multi-tenant architecture
//
// Development: Uses Vite proxy to avoid CORS issues
//   - /medplum -> Self-hosted Medplum
// Production: Uses direct URL from tenant configuration
const tenantConfig = getTenantConfig();
const baseUrl = import.meta.env.VITE_MEDPLUM_BASE_URL ||
  (import.meta.env.DEV ? `${window.location.origin}/medplum` : tenantConfig.medplumBaseUrl);

// Note: Project ID (VITE_MEDPLUM_PROJECT_ID) is used for login redirection
// The access token obtained after login contains all project context for subsequent API calls
// See SignInPage.tsx for OAuth flow that uses the project ID

// Coalesce burst 401s into a single redirect, and never bounce a user who's
// already on the auth flow. Previously `() => (window.location.href = '/')`
// would full-reload on the first transient 401 — a single network blip or a
// stale request mid-refresh would log the user out unexpectedly.
const PUBLIC_AUTH_PATHS = ['/signin', '/register', '/forgot-password', '/reset-password'];
let unauthenticatedRedirectScheduled = false;

function handleUnauthenticated(): void {
  const pathname = window.location.pathname;
  if (PUBLIC_AUTH_PATHS.some((p) => pathname.startsWith(p))) {
    return;
  }
  if (unauthenticatedRedirectScheduled) {
    return;
  }
  unauthenticatedRedirectScheduled = true;
  console.warn('[Auth] Medplum returned unauthenticated — redirecting to sign-in');
  // Small delay lets in-flight 401s settle so we don't redirect mid-refresh.
  // Long enough to skip transient blips, short enough to feel responsive.
  setTimeout(() => {
    window.location.href = '/signin';
  }, 400);
}

const medplum = new MedplumClient({
  onUnauthenticated: handleUnauthenticated,
  baseUrl,
  cacheTime: 60000,
  autoBatchTime: 100,
});

// React Query client for custom forms and other data fetching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

const router = createBrowserRouter([{ path: '*', element: <App /> }]);

const navigate = (path: string): Promise<void> => router.navigate(path);

const container = document.getElementById('root') as HTMLDivElement;
const root = createRoot(container);
root.render(
  <StrictMode>
    <ChunkLoadErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <MedplumProvider medplum={medplum} navigate={navigate}>
          <AppThemeProvider>
            <DynamicMantineProvider>
              <Notifications position="bottom-right" />
              <RouterProvider router={router} />
            </DynamicMantineProvider>
          </AppThemeProvider>
        </MedplumProvider>
      </QueryClientProvider>
    </ChunkLoadErrorBoundary>
  </StrictMode>
);
